import { Box, Flex, Tabs, TabList, Text, IconButton } from "@chakra-ui/react";
import { SearchIcon } from "../../components/icons/SearchIcon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAccountData } from "../../hooks/useAccountData";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { UnderlinedTab } from "../../components/tabs/UnderlinedTab";
import { SearchField } from "../../components/fields/SearchField";
import { useIsSmallBreakpoint, useMobileBreakpoint } from "../../utils/useBreakpoints";

export interface ShelfSearchConfig {
  placeholder: string;
  onSearch: (term: string) => void;
  initValue?: string;
}

export interface ShelfOutletContext {
  setSearchConfig: (config: ShelfSearchConfig | null) => void;
}

const BASE_TABS = [
  { path: "/my-shelf/library", labelKey: "screens.shelf.tabs.library" },
  { path: "/my-shelf/purchases", labelKey: "screens.shelf.tabs.purchases" },
  { path: "/my-shelf/creators", labelKey: "screens.shelf.tabs.creators" },
] as const;

const READING_LISTS_TAB = {
  path: "/my-shelf/reading-lists",
  labelKey: "screens.shelf.tabs.readingLists",
} as const;

const MONTHLY_REWARDS_TAB = {
  path: "/my-shelf/gold-rewards",
  labelKey: "screens.shelf.tabs.monthlyRewards",
} as const;

export default function MyShelfScreen() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accountData = useAccountData();
  const isSmall = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();

  const [searchConfig, setSearchConfig] = useState<ShelfSearchConfig | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  useTitleOnNavigation(t("screens.shelf.pageTitle"));

  // Redirect bare /my-shelf to /my-shelf/library
  useEffect(() => {
    if (
      location.pathname === "/my-shelf" ||
      location.pathname === "/my-shelf/"
    ) {
      navigate("/my-shelf/library", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Reset search value when child provides new initValue
  useEffect(() => {
    setSearchValue(searchConfig?.initValue ?? "");
  }, [searchConfig?.initValue]);

  // Reset search when switching tabs (skip initial mount)
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setSearchValue("");
      setSearchOpen(false);
    }
  }, [location.pathname]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      searchConfig?.onSearch(value);
    },
    [searchConfig],
  );

  const tabs = useMemo(() => {
    const hasReadingLists = !!accountData?.features?.["reading_lists"];
    const hasMonthlyRewards = !!accountData?.features?.["monthly_rewards"];
    const withReadingLists = hasReadingLists
      ? ([...BASE_TABS, READING_LISTS_TAB] as const)
      : BASE_TABS;
    return hasMonthlyRewards
      ? ([...withReadingLists, MONTHLY_REWARDS_TAB] as const)
      : withReadingLists;
  }, [accountData?.features]);

  const activeIndex = useMemo(() => {
    const idx = tabs.findIndex((tab) => location.pathname.startsWith(tab.path));
    return idx >= 0 ? idx : 0;
  }, [location.pathname, tabs]);

  const outletContext = useMemo<ShelfOutletContext>(
    () => ({ setSearchConfig }),
    [setSearchConfig],
  );

  // Auth gate: render nothing until /account/me resolves; bounce anonymous
  // users to / before the first paint of the screen.
  if (accountData === undefined) return null;
  if (!accountData.user) return <Navigate to="/" replace />;

  return (
    <Flex direction="column" width="full" maxW="1200px" mx="auto">
      <Text
        as="h1"
        fontSize={{ base: "24px", lg: "5xl" }}
        lineHeight={{ base: "1.3", lg: "45.6px" }}
        letterSpacing=".5px"
        fontWeight="700"
        fontFamily="Bricolage Grotesque"
        color="dune.dune"
        pt={{ base: 6, lg: 10 }}
        pb={2}
        pl={{ base: "16px", lg: 0 }}
      >
        {t("screens.shelf.title")}
      </Text>
      <Flex
        direction="row"
        align="flex-end"
        justifyContent="space-between"
        bg="charcoal.charcoal"
        pl={{ base: "16px", lg: 0 }}
        pr={{ base: "16px", lg: 0 }}
        mr={{ base: 0, lg: "40px" }}
        borderBottom="1.5px solid"
        borderColor="transparent.white.20"
      >
        <Tabs
          variant="unstyled"
          index={activeIndex}
          onChange={(index) => navigate(tabs[index].path)}
        >
          <TabList gap="16px">
            {tabs.map((tab) => (
              <UnderlinedTab
                key={tab.path}
                fontSize={isMobile ? "14px" : "20px"}
                paddingBottom={isMobile ? "8px" : "12px"}
                marginTop={isMobile ? "4px" : "8px"}
              >
                {t(tab.labelKey)}
              </UnderlinedTab>
            ))}
          </TabList>
        </Tabs>
        <Flex align="center" gap="8px" pb="8px" flexShrink={0}>
          {searchConfig && !isSmall && (
            <Box width="250px">
              <SearchField
                placeholder={searchConfig.placeholder}
                value={searchValue}
                onChange={handleSearchChange}
                onClear={() => handleSearchChange("")}
                isOpen
                clearOnBlur={false}
              />
            </Box>
          )}
          {searchConfig && isSmall && (
            <IconButton
              aria-label="Search"
              icon={<SearchIcon w="20px" h="20px" />}
              variant="ghost"
              color={searchOpen ? "blaze.blaze" : "dune.100"}
              minW="36px"
              h="36px"
              borderRadius="full"
              bg={searchOpen ? "transparent.white.10" : undefined}
              _hover={{ bg: "transparent.white.10" }}
              onClick={() => {
                if (searchOpen) {
                  handleSearchChange("");
                  setSearchOpen(false);
                } else {
                  setSearchOpen(true);
                }
              }}
            />
          )}
        </Flex>
      </Flex>
      {searchConfig && isSmall && searchOpen && (
        <Box px="16px" pt="12px" pb="8px">
          <SearchField
            placeholder={searchConfig.placeholder}
            value={searchValue}
            onChange={handleSearchChange}
            onClear={() => {
              handleSearchChange("");
              setSearchOpen(false);
            }}
            isOpen
            clearOnBlur={false}
          />
        </Box>
      )}
      <Outlet context={outletContext} />
    </Flex>
  );
}
